/* eslint-disable react/no-this-in-sfc */
import React, { useRef, useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { createRoot } from 'react-dom/client';
import moment from 'moment';
import _ from "lodash";
import * as S from './TableChartStyle';

const DEFAULT_COLOR = '#AEE3E5';

export function TableChart({ filters, chartInputData, chartOverrideOptions, unit }) {
  const ref = useRef();

  const chartRef = useRef(null);
  const [hiddenData, setHiddenData] = useState([]);

  useEffect(() => {
    const tooltipEl = document.getElementById('custom-tooltip');
    if (tooltipEl) {
      const root = createRoot(tooltipEl);
      tooltipEl.reactRoot = root;
    }
  }, []);

  const legendClick = (label) => {
    setHiddenData((prev) => (prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]));
  };

  useEffect(() => {
    const handleWheelScroll = (event) => {
      if (ref.current) {
        event.preventDefault();
        ref.current.scrollLeft += event.deltaY;
      }
    };

    const wrapperElement = ref.current;
    if (wrapperElement) {
      wrapperElement.addEventListener('wheel', handleWheelScroll);
    }

    return () => {
      if (wrapperElement) {
        wrapperElement.removeEventListener('wheel', handleWheelScroll);
      }
    };
  }, []);

  const createChartOptions = (date, scalesXMax) => {
    const newChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          intersect: false,
          external: function (context) {
            const tooltipModel = context.tooltip;
            let tooltipEl = document.getElementById('custom-tooltip');
    
            if (!tooltipModel.title || tooltipModel.title[0] === undefined) {
              if (tooltipEl) tooltipEl.style.opacity = 0;
              return;
            }
    
            if (!tooltipEl) {
              tooltipEl = document.createElement('div');
              tooltipEl.id = 'custom-tooltip';
              document.body.appendChild(tooltipEl);
            }
    
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0;
              return;
            }
    
            const position = context.chart.canvas.getBoundingClientRect();
            const dataPointY = tooltipModel.dataPoints[0].element.y;
    
            const tooltipX = position.left + window.scrollX + tooltipModel.caretX - 60;
            const tooltipY = position.top + window.scrollY + dataPointY - tooltipEl.offsetHeight - 20;
    
            tooltipEl.style.opacity = 1;
            tooltipEl.style.left = tooltipX + 'px';
            tooltipEl.style.top = tooltipY + 'px';
    
            const time = tooltipModel.title[0];
            let tooltipDate = date;
            let tooltipTime = time;

            if (time && time.length > 2) {
              tooltipDate = moment(time).format('YYYY.MM.DD');
              tooltipTime = moment(time).format('HH');
            }
    
            let innerHtml = `<div class="row"><p class="title">날짜</p><p class="val">${tooltipDate}</p></div>`;
            innerHtml += `<div class="row"><p class="title">시간</p><p class="val">${tooltipTime}</p></div>`;
            tooltipModel.dataPoints.forEach((dataPoint) => {
              const label = dataPoint.dataset.label || '';
              const value = dataPoint.formattedValue || '';
              innerHtml += `<div class="row"><p class="title">${label}</p><p class="val">${value} ${unit && unit}</p></div>`;
            });
    
            tooltipEl.innerHTML = innerHtml;
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          // max: 2000,
          border: {
            display: false,
          },
          ticks: {
            padding: 10,
            // stepSize: 500,
            font: {
              family: 'Pretendard',
              size: 12,
              weight: '500',
              lineHeight: 1.5,
            },
            color: 'rgb(23, 23, 23, 0.7)',
            callback: function (value) {
              return value.toString();
            },
          },
          grid: {
            drawTicks: false,
          },
        },
        x: {
          min: -0.2,
          max: scalesXMax,
          grid: {
            drawBorder: false,
            borderWidth: 0,
            color: 'transparent',
          },
          ticks: {
            align: 'start',
            font: { family: 'Pretendard', size: 12, weight: '500', lineHeight: 1.5 },
            color: 'rgb(23, 23, 23, 0.7)',
            callback: function (value, index, values) {
              let label = this.getLabelForValue(value);

              if (label) {
                if (label.length > 2) {
                  const hour = moment(label).format('HH');

                  if (hour === '00') {
                    label = moment(label).format('MM.DD HH');
                  } else {
                    label = moment(label).format('HH');
                  }
                }
              }
              return label
            },
          },
        },
      },
      layout: {
        padding: {
          left: 0,
          right: 10,
          top: 10,
          bottom: 0,
        },
      },
    }

    if (chartOverrideOptions) {
      _.merge(newChartOptions, chartOverrideOptions);
    }

    return newChartOptions;
  };

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });
  const [chartOptions, setChartOptions] = useState(createChartOptions(filters.date, 1));

  useEffect(() => {
    const dataItems = [];

    if (chartInputData && chartInputData.data1) dataItems.push(chartInputData.data1);
    if (chartInputData && chartInputData.data2) dataItems.push(chartInputData.data2);
    if (chartInputData && chartInputData.data3) dataItems.push(chartInputData.data3);

    const datasets = dataItems
        .map((dataItem, i) => {
          const tempData = [dataItem.data[0], ...dataItem.data, dataItem.data[dataItem.data.length - 1]];

          return {
            label: dataItem.name,
            data: tempData,
            fill: true,
            backgroundColor: (context) => {
              const gradient = context.chart.ctx.createLinearGradient(0, 0, 0, 265);

              const color = dataItem.color || DEFAULT_COLOR;

              gradient.addColorStop(0, color);
              gradient.addColorStop(0.335, `${color}AA`);
              gradient.addColorStop(1, `${color}00`);

              // if (i === 2) {
              //   gradient.addColorStop(0, '#DBFFD5');
              //   gradient.addColorStop(0.335, 'rgba(219, 255, 213, 0.67)');
              //   gradient.addColorStop(1, 'rgba(219, 255, 213, 0)');
              // }
              // if (i === 1) {
              //   gradient.addColorStop(0, '#FFD3C6');
              //   gradient.addColorStop(0.335, 'rgba(255, 211, 198, 0.67)');
              //   gradient.addColorStop(1, 'rgba(255, 211, 198, 0)');
              // } else if (i === 0) {
              //   gradient.addColorStop(0, '#AEE3E5');
              //   gradient.addColorStop(0.335, 'rgba(223, 244, 245, 0.5)');
              //   gradient.addColorStop(1, 'rgba(223, 244, 245, 0)');
              // }

              return gradient;
            },
            borderColor: dataItem.color || DEFAULT_COLOR,
            tension: 0.4,
            pointRadius: 0,
            borderWidth: 2,
            hidden: hiddenData.includes(dataItem.name),
          };
        })
        .reverse();

    const data = {
      labels: chartInputData && chartInputData.labels ? ['', ...chartInputData.labels, ''] : [],
      datasets: datasets,
    };

    setChartData(data);

    setChartOptions(createChartOptions(filters.date, chartInputData && chartInputData.labels ? (chartInputData.labels.length + 1) : 1));
  }, [chartInputData, hiddenData]);

  return (
    <>
      <S.ChartWrapper ref={ref}>
        <S.ChartContainer $count={chartInputData && chartInputData.labels && chartInputData.labels.length}>
          <Line ref={chartRef} data={chartData} options={chartOptions} />
          <S.StyledTooltipContainer id="custom-tooltip" />
        </S.ChartContainer>
      </S.ChartWrapper>
      <S.CustomLegend>
        {chartInputData && chartInputData.data1 && (
          <S.LegendItem
            onClick={() => legendClick(chartInputData && chartInputData.data1 && chartInputData.data1.name)}
            $selected={hiddenData.includes(chartInputData && chartInputData.data1 && chartInputData.data1.name)}
            $color={chartInputData.data1.color || DEFAULT_COLOR}
          >
            {chartInputData && chartInputData.data1 && chartInputData.data1.name}
          </S.LegendItem>
        )}
        {chartInputData && chartInputData.data2 && (
          <S.LegendItem
            onClick={() => legendClick(chartInputData && chartInputData.data2 && chartInputData.data2.name)}
            $selected={hiddenData.includes(chartInputData && chartInputData.data2 && chartInputData.data2.name)}
            $color={chartInputData.data2.color || DEFAULT_COLOR}
          >
            {chartInputData && chartInputData.data2 && chartInputData.data2.name}
          </S.LegendItem>
        )}
        {chartInputData && chartInputData.data3 && (
          <S.LegendItem
            onClick={() => legendClick(chartInputData && chartInputData.data3 && chartInputData.data3.name)}
            $selected={hiddenData.includes(chartInputData && chartInputData.data3 && chartInputData.data3.name)}
            $color={chartInputData.data3.color || DEFAULT_COLOR}
          >
            {chartInputData && chartInputData.data3 && chartInputData.data3.name}
          </S.LegendItem>
        )}
      </S.CustomLegend>
    </>
  );
}